import React, { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { BranchComparisonBranch } from '../../hooks/useBrandQueries';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor } from '../../utils/themeHelpers';
import { TYPOGRAPHY_CONFIG } from '../../config/typography.config';
import ChartControls from '../ui/ChartControls';
import LoadingSpinner from '../ui/LoadingSpinner';
import EmptyState from '../ui/EmptyState';
import { logger } from '../../utils/logger';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';

interface SimpleComparisonTabProps {
  branches: BranchComparisonBranch[];
  metrics: Array<{ id: string; label: string; unit: string }>;
  loading?: boolean;
  metricAverages?: Record<string, number>;
}

// Key metrics to display in Simple tab
const KEY_METRICS = [
  'revenue',
  'invoiceCount',
  'customerCount',
  'satisfactionScore',
  'subscription',
  'creditInvoiceCount',
  'creditInvoiceAmount',
  'usedCredit'
];

/**
 * Simple Comparison Tab Component
 * 
 * Displays individual bar charts for 4 key metrics with branch values and brand average line
 * Simple and easy to understand comparison view
 */
const SimpleComparisonTab: React.FC<SimpleComparisonTabProps> = ({
  branches,
  metrics,
  metricAverages,
  loading = false
}) => {
  const { currentTheme } = useTheme();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();

  // Memoize chart colors to prevent unnecessary recalculations
  const chartColors = useMemo(() => getChartColors(currentTheme), [currentTheme]);

  // Memoize branch colors with proper opacity
  const branchColors = useMemo(() =>
    branches.map((_, index) => getChartBackgroundColor(index, currentTheme, 0.8)),
    [branches.length, currentTheme]
  );

  // Memoize border colors (full opacity)
  const branchBorderColors = useMemo(() =>
    branches.map((_, index) => chartColors[index % chartColors.length]),
    [branches.length, chartColors]
  );

  // Filter metrics to only show key metrics
  const filteredMetrics = useMemo(() =>
    metrics.filter(m => KEY_METRICS.includes(m.id)),
    [metrics]
  );

  const avgCurrency = useMemo(() => {
    const currencies = branches
      .map(branch => branch.currency)
      .filter((currency): currency is string => !!currency);

    const uniqueCurrencies = Array.from(new Set(currencies));
    if (uniqueCurrencies.length === 1) {
      return uniqueCurrencies[0];
    }

    return 'تومان';
  }, [branches]);

  // Create refs for each metric chart
  const chartRefs = useMemo(() => {
    const refs: Record<string, React.RefObject<HTMLDivElement>> = {};
    filteredMetrics.forEach(metric => {
      refs[metric.id] = React.createRef<HTMLDivElement>();
    });
    return refs;
  }, [filteredMetrics]);

  if (loading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-1">
        <div className="flex flex-col items-center justify-center h-64">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری نمودارها…"
          />
        </div>
      </div>
    );
  }

  if (!branches || branches.length === 0) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-1">
        <EmptyState message="داده‌ای برای نمایش موجود نیست" />
      </div>
    );
  }

  const renderMetricChart = (metric: { id: string; label: string; unit: string }) => {
    const chartRef = chartRefs[metric.id];
    const componentId = `branch-comparison-${metric.id}`;
    const componentTitle = `مقایسه ${metric.label}`;

    try {
      // Get values for this metric from all branches
      const values = branches.map(branch => branch.metrics[metric.id] || 0);
      const labels = branches.map(branch => branch.branchName);

      const avg = metricAverages?.[metric.id] ?? 0;

      // Log chart rendering
      logger.info(`Rendering chart for metric: ${metric.id}`, 'SimpleComparisonTab', {
        metricId: metric.id,
        branchCount: branches.length,
        average: avg
      });

      const chartData = {
        labels: labels,
        datasets: [
          {
            label: metric.label,
            data: values,
            backgroundColor: branchColors.slice(0, branches.length),
            borderColor: branchBorderColors.slice(0, branches.length),
            borderWidth: 2,
            borderRadius: 6,
            barThickness: 30,
            type: 'bar' as const,
            order: 2,
          },
          {
            label: 'میانگین برند',
            data: Array(branches.length).fill(avg),
            type: 'line' as const,
            borderColor: '#EAB308', // yellow-500
            backgroundColor: 'transparent',
            borderWidth: 2,
            borderDash: [8, 4],
            pointRadius: 0,
            pointHoverRadius: 0,
            order: 1,
            tension: 0,
          }
        ]
      };

      // Get base chart options from theme helper
      const baseOptions = getChartJsOptions(currentTheme);

      const chartOptions = {
        ...baseOptions,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          ...baseOptions.plugins,
          legend: {
            display: false,
          },
          tooltip: {
            ...baseOptions.plugins?.tooltip,
            filter: function (tooltipItem: any) {
              // Hide tooltip for brand average line (dataset index 1)
              return tooltipItem.datasetIndex === 0;
            },
            callbacks: {
              ...(baseOptions.plugins?.tooltip?.callbacks || {}),
              label: function (context: any) {
                const branchIndex = context.dataIndex;
                const branch = branches[branchIndex];
                let label = context.parsed.y.toLocaleString('fa-IR');

                // Add unit based on metric type
                if (metric.unit === 'percentage') {
                  label += '%';
                } else if (metric.unit === 'currency') {
                  label += ` ${branch.currency || avgCurrency}`;
                } else if (metric.unit === 'score') {
                  label += '%';
                }

                return label;
              },
              afterBody: function () {
                let avgLabel = avg.toLocaleString('fa-IR');

                if (metric.unit === 'percentage') {
                  avgLabel += '%';
                } else if (metric.unit === 'currency') {
                  avgLabel += ` ${avgCurrency}`;
                } else if (metric.unit === 'score') {
                  avgLabel += '%';
                }

                return `میانگین برند: ${avgLabel}`;
              }
            }
          }
        },
        scales: {
          x: {
            ...baseOptions.scales?.x,
            title: {
              // display: true,
              text: 'شعبه',
              color: 'var(--chart-axis-color)',
              font: {
                family: 'var(--font-primary)',
                size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
                weight: 600,
              },
            },
            ticks: {
              ...getChartJsOptions(currentTheme).scales?.x?.ticks,
              autoSkip: false,
              maxRotation: 45,
              minRotation: 45,
              // padding: 5,
            },
          },
          y: {
            ...baseOptions.scales?.y,
            beginAtZero: true,
            title: {
              //  display: true,
              text: metric.label,
              color: getChartColors(currentTheme)[0],
              padding: { top: 10, bottom: 0 },
              font: {
                family: 'var(--font-primary)',
                size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
                weight: 600,
              },
            },
            ticks: {
              ...getChartJsOptions(currentTheme).scales?.y?.ticks,
              callback: function (value: any) {
                let formatted = value.toLocaleString('fa-IR');
                if (metric.unit === 'percentage' || metric.unit === 'score') {
                  formatted += '%';
                }
                //  else if (metric.unit === 'score') {
                //   formatted += '/۱۰۰';
                // }
                return formatted;
              }
            },
            // ticks: {
            //   ...baseOptions.scales?.y?.ticks,
            //   font: {
            //     family: 'var(--font-primary)',
            //     size: TYPOGRAPHY_CONFIG.components.chart.axisTick.size,
            //     weight: TYPOGRAPHY_CONFIG.components.chart.axisTick.weight,
            //   },
            //   callback: function(value: any) {
            //     let formatted = value.toLocaleString('fa-IR');
            //     if (metric.unit === 'percentage') {
            //       formatted += '%';
            //     } else if (metric.unit === 'score') {
            //       formatted += '/۱۰۰';
            //     }
            //     return formatted;
            //   }
            // },
          }
        }
      } as any;

      return (
        <div key={metric.id} ref={chartRef} className="card-base card-padding animate-enter">
          <ChartControls
            componentId={componentId}
            componentTitle={componentTitle}
            componentType="chart"
            dashboardSource="branchComparison"
            onInfo={() => {
              // Could open a modal with chart info
              console.log('Chart info for:', metric.label);
            }}
            onFavorite={() => handleFavoriteToggle({
              componentId,
              componentTitle,
              dashboardSource: 'branchComparison',
              componentType: 'chart',
              description: `مقایسه ${metric.label} بین شعب`,
              props: { metricId: metric.id },
              gridLayout: {
                containerClass: 'grid grid-cols-1 gap-6'
              }
            })}
            isFavorited={isFavorited(componentId)}
            chartRef={chartRef}
            className="mb-4"
          />

          {/* <div className="flex items-center justify-between mb-4">
            <h3 className="title-card text-center text-text-primary">{metric.label}</h3>
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <span className="w-3 h-0.5 bg-yellow-500"></span>
              <span>میانگین برند</span>
            </div>
          </div> */}

          <div className="mb-6">
        <h3 className="title-card">{metric.label}</h3>
        {/* <p className="text-text-secondary text-sm mt-1 text-center">
         مقایسه نرخ جذب و ریزش مشتریان 
        </p> */}
      </div>

          <div style={{ height: 400, width: '100%' }} role="img" aria-label={`نمودار مقایسه ${metric.label} برای ${branches.length} شعبه`}>
            <Chart type='bar' data={chartData} options={chartOptions} />
          </div>

          <div className="mt-4 pt-4 border-t border-border-default">
            <div className="flex items-center justify-center gap-2 text-sm text-text-secondary">
              <span className="inline-flex items-center gap-2">
                <span className="w-8 h-0.5 bg-yellow-500"></span>
                <span>میانگین برند: {avg.toLocaleString('fa-IR')}</span>
                  <span className="font-medium text-text-primary">
                  {metric.unit === 'percentage' ? '%' : metric.unit === 'currency' ? ` ${avgCurrency}` : metric.unit === 'score' ? '/۱۰۰' : ''}
                </span>
              </span>
            </div>
          </div>
        </div>
      );
    } catch (error) {
      logger.error(`Failed to render chart for metric: ${metric.id}`, 'SimpleComparisonTab', error);
      return (
        <div key={metric.id} className="card-base card-padding animate-enter">
          <h3 className="title-card text-text-primary mb-4">{metric.label}</h3>
          <div className="flex flex-col items-center justify-center h-[400px]">
            <div className="text-text-secondary mb-2">⚠️</div>
            <div className="text-text-secondary">خطا در نمایش نمودار</div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="space-y-6 animate-enter-up">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredMetrics.map((metric, index) => (
          <div key={metric.id} className={`animate-delay-${Math.min(index + 1, 3)}`}>
            {renderMetricChart(metric)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SimpleComparisonTab;
